import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import SupportIcon from '@mui/icons-material/CommentOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';
import clsx from 'clsx';
// import * as authActions from 'app/auth/store/actions';
import FeedbackDialog from 'app/components/FeedbackDialog';
import { LicenseGroupLineIcon, LogoutIcon, SettingsIcon, ProfileIcon, NotificationsIcon } from 'app/components/Icons';
import useGetPageSection from 'app/hooks/useGetPageSection';
import { useDispatch, useSelector } from 'app/modules/react-redux';
import * as Actions from 'app/store/actions';
import {
	getProfile,
	getRolePermissionCheck,
	getSelectedLicenseGroupId,
	getHasManagerPage,
	getUserPreferences,
	getPermissions,
	getSelectedLicenseGroupData,
} from 'app/store/reducers';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { deepSome, getHelpUrl, getRootDomain } from 'app/utils/helpers';
import getHelpContextUrl from 'app/utils/getHelpContextUrl';
import { licenseGroupPlanDesignCheck } from 'app/utils/tenant-plan';

const UserMenu = ({ adminPage = false }: { adminPage: boolean }) => {
	const dispatch = useDispatch();
	const { t, i18n } = useTranslation();
	const profile = useSelector(getProfile);
	const permissions = useSelector(getPermissions);
	const userPreferences = useSelector(getUserPreferences);
	// const adminedLicenseGroups = useSelector(getAdminedLicenseGroups) as
	// 	| ReturnType<typeof getAdminedLicenseGroups>
	// 	| undefined; // HACK::use actual type here - assume never undefined within the wrapped app
	const hasManagerPage = useSelector(getHasManagerPage);
	const selectedLicenseGroupId = useSelector(getSelectedLicenseGroupId) as
		| ReturnType<typeof getSelectedLicenseGroupId>
		| undefined; // HACK::use actual type here - assume never undefined within the wrapped app

	const pageSection = useGetPageSection();
	const rolePermissionCheck = useSelector(getRolePermissionCheck);
	const licenseGroupData = useSelector(getSelectedLicenseGroupData) as
		| ReturnType<typeof getSelectedLicenseGroupData>
		| undefined; // HACK::use actual type here - assume never undefined within the wrapped app

	useEffect(() => {
		dispatch(Actions.getManagedLicenseGroups());
		dispatch(Actions.getTenantManagerGroups());
	}, [dispatch]);

	const [userMenu, setUserMenu] = useState<HTMLButtonElement | null>(null);

	const userMenuClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		setUserMenu(event.currentTarget);
	};

	const onLogout = () => {
		window.sessionStorage.clear();
	};

	const userMenuClose = () => {
		setUserMenu(null);
	};

	const { pathname } = useLocation();
	const noPermissions = !deepSome(permissions);

	return (
		<>
			<Button
				className={clsx('h-64 px-20 starter:text-white landing-toolbar-text-white')}
				onClick={userMenuClick}
			>
				<Avatar className="text-theme-secondary bg-white">{profile.firstName[0]}</Avatar>

				<div className="flex-col mx-12 items-start">
					<Typography
						component="span"
						className="normal-case font-600 flex"
						style={userPreferences?.appearance === 'dark' ? { color: 'white' } : {}}
					>
						{t('header:welcome name', { name: profile.firstName })}
					</Typography>
				</div>

				<ExpandMoreIcon
					className={clsx('transition-transform', { 'rotate-x-180': !!userMenu })}
					style={userPreferences?.appearance === 'dark' ? { color: 'white' } : {}}
				/>
			</Button>

			<Popover
				keepMounted
				open={!!userMenu}
				anchorEl={userMenu}
				onClose={userMenuClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'center',
				}}
				classes={{
					paper: 'py-8 starter:text-white landing-toolbar-text-white starter:bg-theme-secondary-900 rounded-none rounded-b-8',
				}}
			>
				{/* {(pageSection.admin || pageSection.publicProfile) && (
					<MenuItem component={Link} to="/welcome" onClick={userMenuClose} role="button">
						<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
							<Icon>storefront</Icon>
						</ListItemIcon>
						<ListItemText primary={t('user menu:home')} />
					</MenuItem>
				)} */}

				{/* {!pageSection.admin && adminedLicenseGroups && adminedLicenseGroups.length > 0 && (
					<MenuItem component={Link} to="/" onClick={userMenuClose} role="button">
						<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
							<LicenseGroupLineIcon />
						</ListItemIcon>
						<ListItemText primary={t('user menu:admin')} />
					</MenuItem>
				)} */}

				{!pageSection.manager &&
					hasManagerPage &&
					// checking if I'm not in a subdomain to decide if I can just push to page
					(window.location.host === getRootDomain() ? (
						<MenuItem component={Link} to="/manager/tenants" onClick={userMenuClose} role="button">
							<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
								<span style={{ color: 'transparent' }}>
									<LicenseGroupLineIcon
										htmlColor={
											licenseGroupPlanDesignCheck('stratus') ? 'rgba(0, 0, 0, 0.54)' : 'white'
										}
									/>
								</span>
							</ListItemIcon>
							<ListItemText primary={t('user menu:tenant manager')} />
						</MenuItem>
					) : (
						<a href={`${window.location.protocol}//${getRootDomain()}/manager/tenants`} role="button">
							<MenuItem>
								<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
									<span style={{ color: 'transparent' }}>
										<LicenseGroupLineIcon
											htmlColor={
												userPreferences?.appearance === 'light' &&
												licenseGroupData?.plan?.includes('stratus')
													? 'rgba(0, 0, 0, 0.54)'
													: 'white'
											}
										/>
									</span>
								</ListItemIcon>
								<ListItemText primary={t('user menu:tenant manager')} />
							</MenuItem>
						</a>
					))}

				{/* <ComingSoonWrapper>
					<MenuItem component={Link} to="/about" onClick={userMenuClose} role="button">
						<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
							<InfoIcon />
						</ListItemIcon>
						<ListItemText primary={t('user menu:about')} />
					</MenuItem>
				</ComingSoonWrapper> */}

				{!noPermissions &&
					(pageSection.licenseGroupDomain ? (
						<MenuItem component={Link} to="/admin/profile" onClick={userMenuClose} role="button">
							<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
								<ProfileIcon />
							</ListItemIcon>
							<ListItemText primary={t('user menu:admin profile')} />
						</MenuItem>
					) : (
						<MenuItem component={Link} to="/profile" onClick={userMenuClose} role="button">
							<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
								<ProfileIcon />
							</ListItemIcon>
							<ListItemText primary={t('user menu:public profile')} />
						</MenuItem>
					))}
				<FeedbackDialog>
					<MenuItem onClick={userMenuClose} role="button">
						<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
							<SupportIcon />
						</ListItemIcon>
						<ListItemText primary={t('user menu:feedback')} />
					</MenuItem>
				</FeedbackDialog>

				<a
					href={getHelpUrl(i18n.language, getHelpContextUrl(pathname))}
					target="_blank"
					rel="noreferrer noopener"
					role="button"
				>
					<MenuItem>
						<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
							<HelpIcon />
						</ListItemIcon>
						<ListItemText primary={t('Help')} />
					</MenuItem>
				</a>

				{pageSection.admin &&
					selectedLicenseGroupId &&
					rolePermissionCheck('settingsTab.systemSection.manageNotifications') && (
						<MenuItem component={Link} to="/admin/notifications" onClick={userMenuClose} role="button">
							<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
								<NotificationsIcon />
							</ListItemIcon>
							<ListItemText primary={t('Notifications')} />
						</MenuItem>
					)}

				{pageSection.admin &&
					selectedLicenseGroupId &&
					rolePermissionCheck('settingsTab.settingsSection.view') && (
						<MenuItem component={Link} to="/admin/settings" onClick={userMenuClose} role="button">
							<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
								<SettingsIcon />
							</ListItemIcon>
							<ListItemText primary={t('Settings')} />
						</MenuItem>
					)}

				<a href="/api/sso/logout" onClick={onLogout} role="button">
					<MenuItem>
						<ListItemIcon className="min-w-40 starter:text-white landing-toolbar-text-white">
							<LogoutIcon />
						</ListItemIcon>
						<ListItemText primary={t('Logout')} />
					</MenuItem>
				</a>
			</Popover>
		</>
	);
};

export default UserMenu;
